import { useMemo } from 'react';
import { Tag, Tooltip, Typography } from 'antd';
import {
  SmartDataTableProps,
  SmartTableColumn,
} from '@src/components/SmartDataTable/types';
import { useTypeRender } from './useTypeRender';
import { useGenderRender } from './useGenderRender';
import { useStatusRender } from './useStatusRender';
import { useConversionStatusRender } from './useConversionStatusRender';
import { useScheduledDateRender, useUserTimestampRender } from '@src/hooks';
import {
  TYPE_FILTER_OPTIONS,
  GENDER_FILTER_OPTIONS,
  PATIENT_STATUS_FILTER_OPTIONS,
  CONVERSION_STATUS_FILTER_OPTIONS,
} from '../constants';
import dayjs from 'dayjs';
import { FaWhatsapp } from 'react-icons/fa';

const { Text } = Typography;

interface UsePatientsColumnsOptions {
  showCreatedAt?: boolean;
}

export const usePatientsColumns = (
  options: UsePatientsColumnsOptions = {}
): SmartTableColumn[] => {
  const { showCreatedAt = true } = options;
  const getTypeConfig = useTypeRender();
  const getGenderConfig = useGenderRender();
  const getPatientStatusConfig = useStatusRender();
  const getConversionStatusConfig = useConversionStatusRender();
  const renderFollowUpDate = useScheduledDateRender();
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
  });

  // Handle patient info click to redirect to patient details
  const handlePatientInfoClick = (record: any, e: any) => {
    e.stopPropagation();
    const url = `${window.location.origin}/patients/${record.id}`;
    window.open(url, '_blank');
  };

  return useMemo(() => {
    const baseColumns: SmartTableColumn[] = [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        sorter: true,
        minWidth: 80,
        ellipsis: true,
        render: (value: any) => (
          <Text
            style={{
              fontSize: '12px',
              color: '#8c8c8c',
              backgroundColor: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            #{value}
          </Text>
        ),
      },
      {
        key: 'patient_info',
        title: 'Patient Info',
        dataIndex: 'first_name',
        sorter: false,
        minWidth: 200,
        render: (firstName: any, record: any) => (
          <div
            onClick={e => handlePatientInfoClick(record, e)}
            style={{
              cursor: 'pointer',
              padding: '2px',
              borderRadius: '6px',
              transition: 'all 0.2s ease',
              border: '1px solid transparent',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
              e.currentTarget.style.borderColor = '#d9d9d9';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderColor = 'transparent';
            }}
            title="Click to view patient details"
          >
            <div style={{ fontWeight: 500, color: '#262626' }}>
              {`${firstName || ''} ${record.last_name || ''}`.trim() || 'N/A'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div
                style={{ fontWeight: 500, fontSize: '13px', color: '#8c8c8c' }}
              >
                +{record.mobile_no || 'No mobile'}
              </div>
              {record.mobile_no && (
                <Tooltip title="Open WhatsApp">
                  <FaWhatsapp
                    style={{ fontSize: '18px', color: '#25D366' }}
                    onClick={e => {
                      e.stopPropagation();
                      if (record.mobile_no) {
                        window.open(
                          `https://web.whatsapp.com/send/?phone=${record.mobile_no}`,
                          '_blank'
                        );
                      }
                    }}
                  />
                </Tooltip>
              )}
            </div>
          </div>
        ),
      },
      {
        key: 'gender',
        title: 'Gender',
        dataIndex: 'gender',
        sorter: false,
        minWidth: 100,
        filterable: true,
        filterType: 'multiselect' as const,
        filterOptions: [...GENDER_FILTER_OPTIONS],
        render: (gender: string) => {
          if (!gender) return '-';
          return getGenderConfig(gender);
        },
      },
      {
        key: 'type',
        title: 'Type',
        dataIndex: 'type',
        sorter: true,
        minWidth: 120,
        filterable: true,
        filterType: 'multiselect' as const,
        filterOptions: [...TYPE_FILTER_OPTIONS],
        render: (type: string) => {
          if (!type) return '-';
          return getTypeConfig(type);
        },
      },
      {
        key: 'patient_status',
        title: 'Patient Status',
        dataIndex: 'patient_status',
        sorter: true,
        minWidth: 120,
        filterable: true,
        filterType: 'multiselect' as const,
        filterOptions: [...PATIENT_STATUS_FILTER_OPTIONS],
        render: (patientStatus: string) => {
          if (!patientStatus) return '-';
          return getPatientStatusConfig(patientStatus);
        },
      },
      {
        key: 'conversion_status',
        title: 'Conversion Status',
        dataIndex: 'conversion_status',
        sorter: true,
        minWidth: 120,
        filterable: true,
        filterType: 'multiselect' as const,
        filterOptions: [...CONVERSION_STATUS_FILTER_OPTIONS],
        render: (conversionStatus: string) => {
          if (!conversionStatus) return '-';
          return getConversionStatusConfig(conversionStatus);
        },
      },
      {
        key: 'birth_date',
        title: 'Birth Date',
        dataIndex: 'birth_date',
        sorter: true,
        minWidth: 120,
        render: (birthDate: string, record: any) => (
          <>
            <div>
              {birthDate ? (
                <div style={{ fontSize: '13px', color: '#262626' }}>
                  {dayjs(birthDate).format('DD MMM YYYY')}
                </div>
              ) : (
                '-'
              )}
            </div>
            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
              Age: {record.age} years
            </div>
          </>
        ),
      },
      {
        key: 'follow_up_date',
        title: 'Follow Up',
        dataIndex: 'follow_up_date',
        sorter: true,
        minWidth: 140,
        filterable: true,
        filterType: 'daterange' as const,
        render: (followUpDate: any, record: any) =>
          renderFollowUpDate(followUpDate, record.status),
      },
      {
        key: 'user_timestamp',
        title: 'User & Timestamp',
        dataIndex: 'user_info',
        sorter: false,
        minWidth: 200,
        render: (value: any, record: any) => userTimestampRender(record),
      },
    ];

    // Conditionally add the created_at column with date range filter
    if (showCreatedAt) {
      baseColumns.push({
        key: 'created_at',
        title: 'Created Date',
        dataIndex: 'created_at',
        sorter: true,
        minWidth: 140,
        filterable: true,
        filterType: 'daterange' as const,
        render: () => null, // Hide the column content but keep filter functionality
        hidden: true, // Hide the column from display
      });
    }

    return baseColumns;
  }, [
    getTypeConfig,
    getGenderConfig,
    renderFollowUpDate,
    userTimestampRender,
    showCreatedAt,
  ]);
};
